<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import DOMPurify from 'dompurify';
import { domPurifyConfig } from 'shared/helpers/HTMLSanitizer.js';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { MESSAGE_VARIANTS } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useChatFromPhoneNumber } from 'dashboard/composables/useChatFromPhoneNumber';
import { useMapGetter } from 'dashboard/composables/store';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
});

const { variant, id } = useMessageContext();
const { t } = useI18n();
const activeConversationSearch = useMapGetter(
  'conversationSearch/getActiveConversationSearch'
);
const { startChat, menuItems } = useChatFromPhoneNumber();

const showMenu = ref(false);
const menuPosition = ref({ top: 0, left: 0 });
const selectedPhone = ref('');

const formattedContent = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return props.content;
  }
  return new MessageFormatter(props.content).formattedMessage;
});

const PHONE_NUMBER_REGEX = /\+?\d[\d\s\-().]{6,}\d/g;
const GOOGLE_MAPS_URL_REGEX =
  /https?:\/\/(?:www\.)?(?:maps\.google\.com|google\.com\/maps)\/?\?q=[\d\-.,]+/g;

const wrapPhoneNumbersInNode = node => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent;
    const matches = [...text.matchAll(PHONE_NUMBER_REGEX)];
    if (!matches.length) return;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    matches.forEach(match => {
      const phone = match[0];
      const index = match.index;
      if (index > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, index))
        );
      }
      const link = document.createElement('a');
      link.href = `tel:${phone}`;
      link.className = 'phone-number-link';
      link.dataset.phone = phone;
      link.textContent = phone;
      fragment.appendChild(link);
      lastIndex = index + phone.length;
    });
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    node.replaceWith(fragment);
  } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'A') {
    Array.from(node.childNodes).forEach(wrapPhoneNumbersInNode);
  }
};

const wrapMapLinksInNode = node => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent;
    const matches = [...text.matchAll(GOOGLE_MAPS_URL_REGEX)];
    if (!matches.length) return;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    matches.forEach(match => {
      const url = match[0];
      const index = match.index;
      if (index > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, index))
        );
      }
      const link = document.createElement('a');
      link.href = url;
      link.className = 'map-link';
      link.target = '_blank';
      link.rel = 'noreferrer noopener nofollow';
      link.textContent = url;
      fragment.appendChild(link);
      lastIndex = index + url.length;
    });
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    node.replaceWith(fragment);
  } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'A') {
    Array.from(node.childNodes).forEach(wrapMapLinksInNode);
  }
};

const isActiveSearchMessage = computed(
  () =>
    activeConversationSearch.value?.messageId &&
    Number(activeConversationSearch.value.messageId) === Number(id?.value)
);

const searchQuery = computed(
  () => activeConversationSearch.value?.query?.trim() || ''
);

const escapeRegExp = string => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const ACCENT_MAP = {
  a: '[aáàäâ]',
  e: '[eéèëê]',
  i: '[iíìïî]',
  o: '[oóòöô]',
  u: '[uúùüû]',
  n: '[nñ]',
  A: '[AÁÀÄÂaáàäâ]',
  E: '[EÉÈËÊeéèëê]',
  I: '[IÍÌÏÎiíìïî]',
  O: '[OÓÒÖÔoóòöô]',
  U: '[UÚÙÜÛuúùüû]',
  N: '[NÑnñ]',
};

const buildAccentInsensitiveRegex = query =>
  escapeRegExp(query).replace(
    /[aeiouAEIOUÑñ]/g,
    char => ACCENT_MAP[char] || char
  );

const wrapSearchQueryInNode = (node, query) => {
  if (!query) return;
  const pattern = buildAccentInsensitiveRegex(query);
  const regex = new RegExp(`(${pattern})`, 'gi');
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent;
    const matches = [...text.matchAll(regex)];
    if (!matches.length) return;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    matches.forEach(match => {
      const term = match[0];
      const index = match.index;
      if (index > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, index))
        );
      }
      const mark = document.createElement('mark');
      mark.className = 'bg-n-amber-300 text-n-slate-900 rounded-sm px-0.5';
      mark.textContent = term;
      fragment.appendChild(mark);
      lastIndex = index + term.length;
    });
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    node.replaceWith(fragment);
  } else if (
    node.nodeType === Node.ELEMENT_NODE &&
    !['A', 'MARK'].includes(node.tagName)
  ) {
    Array.from(node.childNodes).forEach(child =>
      wrapSearchQueryInNode(child, query)
    );
  }
};

const formatContent = html => {
  if (!html) return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  if (menuItems.value.length > 0) {
    wrapPhoneNumbersInNode(doc.body);
    wrapMapLinksInNode(doc.body);
  }
  if (isActiveSearchMessage.value && searchQuery.value) {
    wrapSearchQueryInNode(doc.body, searchQuery.value);
  }
  return doc.body.innerHTML;
};

const safeContent = computed(() => {
  const raw = formattedContent.value;
  const sanitized = DOMPurify.sanitize(raw, domPurifyConfig);
  return formatContent(sanitized);
});

const sectionItems = computed(() => [
  {
    title: t('PHONE_CHAT.CHAT_WITH', { phone: selectedPhone.value }),
    description: t('PHONE_CHAT.SELECT_INBOX_HINT'),
    items: menuItems.value,
  },
]);

const isExternalUrl = url => /^https?:\/\//i.test(url);

const handleContentClick = event => {
  const link = event.target.closest('a');
  if (!link) return;

  if (link.classList.contains('phone-number-link')) return;

  const href = link.getAttribute('href') || '';
  if (isExternalUrl(href)) {
    event.preventDefault();
    event.stopPropagation();
    window.open(href, '_blank');
  }
};

const handleClick = event => {
  const link = event.target.closest('.phone-number-link');
  if (!link) return;

  event.preventDefault();
  const phone = link.getAttribute('data-phone');
  if (!phone) return;

  selectedPhone.value = phone;

  if (menuItems.value.length === 1) {
    startChat({ phone, inbox: menuItems.value[0] });
    return;
  }

  const panel =
    link.closest('.conversation-panel') || link.closest('.conversation-wrap');
  const panelRect = panel
    ? panel.getBoundingClientRect()
    : { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
  menuPosition.value = {
    top: panelRect.top + panelRect.height / 2 + window.scrollY,
    left: panelRect.left + panelRect.width / 2 + window.scrollX,
  };
  showMenu.value = true;
};

const closeMenu = () => {
  showMenu.value = false;
};

const handleAction = ({ value, ...rest }) => {
  const inbox =
    menuItems.value.find(item => item.id === value || item.value === value) ||
    rest;
  startChat({ phone: selectedPhone.value, inbox });
  showMenu.value = false;
};
</script>

<template>
  <span
    class="prose prose-bubble"
    @click.capture="handleContentClick"
    @click="handleClick"
    v-html="safeContent"
  />
  <Teleport to="body">
    <div
      v-if="showMenu"
      v-on-click-outside="closeMenu"
      class="fixed z-50 transform -translate-x-1/2 -translate-y-1/2"
      :style="{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }"
    >
      <DropdownMenu
        :menu-sections="sectionItems"
        :show-search="menuItems.length > 5"
        class="min-w-56 max-w-xs !relative"
        @action="handleAction"
      />
    </div>
  </Teleport>
</template>
